<template>
  <div class="month-period-picker">
    <div v-if="!readonly" class="picker-wrapper date-grid-wrapper">
      <div class="picker-header date-grid-header">
        <Button
          type="button"
          variant="secondary"
          icon="fas fa-chevron-left"
          @click="previousYear"
          class="picker-nav-btn date-grid-nav-btn"
          :disabled="isPreviousYearDisabled || readonly"
          size="sm"
          aria-label="Année précédente"
        />
        <h5 class="picker-year">{{ displayedYear }}</h5>
        <Button
          type="button"
          variant="secondary"
          icon="fas fa-chevron-right"
          @click="nextYear"
          class="picker-nav-btn date-grid-nav-btn"
          :disabled="readonly"
          size="sm"
          aria-label="Année suivante"
        />
      </div>
      <div class="picker-grid date-grid">
        <button
          v-for="month in months"
          :key="month.period"
          type="button"
          :disabled="month.disabled || readonly"
          :aria-pressed="isPeriodSelected(month.period) ? 'true' : 'false'"
          :aria-label="formatPeriodLabel(month.period)"
          :class="[
            'picker-month',
            'date-grid-cell',
            {
              'picker-month-selected': isPeriodSelected(month.period),
              'date-grid-cell-selected': isPeriodSelected(month.period),
              'picker-month-disabled': month.disabled || readonly,
              'date-grid-cell-disabled': month.disabled || readonly,
              'picker-month-readonly': readonly,
              'date-grid-cell-readonly': readonly
            }
          ]"
          @click="!readonly && !month.disabled && togglePeriod(month.period)"
        >
          {{ month.label }}
        </button>
      </div>
    </div>
    <div
      v-if="sortedSelectedPeriods.length > 0"
      class="selected-periods date-grid-summary"
      :class="{ 'date-grid-summary--flat': readonly }"
    >
      <strong>{{ readonly ? 'Périodes retenues' : 'Périodes sélectionnées :' }}</strong>
      <div class="selected-periods-list date-grid-summary-list">
        <span
          v-for="period in sortedSelectedPeriods"
          :key="period"
          class="selected-period-tag date-grid-tag"
        >
          {{ formatPeriodLabel(period) }}
          <Button
            v-if="!readonly"
            type="button"
            variant="close"
            icon="fas fa-times"
            @click="removePeriod(period)"
            class="remove-period-btn date-grid-tag-remove-btn"
            :aria-label="`Retirer la période ${formatPeriodLabel(period)}`"
            size="sm"
          />
        </span>
      </div>
    </div>
    <p v-else-if="!readonly" class="empty-state-hint">
      Aucun mois sélectionné pour l'instant.
    </p>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import '../../styles/date-grid-picker.css'
import { useMonthPeriods } from '../../composables/calendar/useMonthPeriods.js'
import { Button } from '../ui'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  readonly: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const selectedPeriods = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const {
  displayedYear,
  months,
  isPeriodSelected,
  togglePeriod,
  removePeriod,
  previousYear,
  nextYear,
  isPreviousYearDisabled,
  sortedSelectedPeriods,
  formatPeriodLabel
} = useMonthPeriods(selectedPeriods)
</script>

<style scoped>
/* Styles communs à la grille (jours/mois) partagés avec AvailabilityCalendar :
   voir src/styles/date-grid-picker.css. Seules les spécificités du sélecteur
   de mois (colonnes, padding de la cellule, titre année) restent ici. */

.month-period-picker {
  margin: var(--spacing-4) 0;
}

.picker-year {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-primary);
}

.picker-grid {
  grid-template-columns: repeat(4, 1fr);
}

.picker-month {
  padding: var(--spacing-md) var(--spacing-sm);
}

.empty-state-hint {
  margin: 0.75rem 0 0;
  font-size: 0.875rem;
  color: var(--color-text-tertiary);
  font-style: italic;
}

@media (max-width: 768px) {
  .picker-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>
